import React, { useState } from 'react';
import { ChevronDown, Phone, MapPin, Clock, Shield } from 'lucide-react';

const FAQ = () => {
    const [openIndex, setOpenIndex] = useState(null);

    const faqs = [
        {
            question: 'Is the massage table and equipment hygienic?',
            answer: 'Absolutely! We use disposable bed sheets and towels for every session. The massage table is sanitized before and after each use. All equipment is maintained to the highest hygiene standards.',
            icon: <Shield size={24} />,
        },
        {
            question: 'What should I prepare at home for the massage session?',
            answer: 'Just ensure a private room with enough space for the massage table (approx 6x3 feet). We bring everything else - table, oils, disposable sheets, towels, and all equipment. No preparation needed from your side!',
            icon: <MapPin size={24} />,
        },
        {
            question: 'Can you do massage for elderly, pregnant women, or people with health conditions?',
            answer: 'Yes! We have specialized training and 15+ years of experience with diverse clients including paralyzed patients, aged individuals, pregnant women, and those with specific health conditions. Please inform us about any health concerns when booking.',
            icon: <Shield size={24} />,
        },
        {
            question: 'What types of oils do you use?',
            answer: 'We use 3 types of premium oils: Ayurvedic herbal oil, Almond oil, and specialized massage oils. All oils are of high quality and suitable for different massage types. You can also request specific oils if you have preferences.',
            icon: <Shield size={24} />,
        },
        {
            question: 'How do I book a massage session?',
            answer: 'Simply call or WhatsApp us at +91-9901853969. Share your preferred date, time, service type, and location. We will confirm your booking immediately.',
            icon: <Phone size={24} />,
        },
        {
            question: 'Do you offer same-day bookings?',
            answer: 'Yes! We accept same-day bookings subject to availability. Call us in the morning and we can usually accommodate you the same day.',
            icon: <Clock size={24} />,
        },
        {
            question: 'What are your working hours?',
            answer: 'We operate from 8:00 AM to 10:00 PM, 7 days a week. Evening slots (6 PM - 10 PM) fill up quickly, so we recommend booking in advance.',
            icon: <Clock size={24} />,
        },
        {
            question: 'What payment methods do you accept?',
            answer: 'We accept Cash, UPI (Google Pay, PhonePe, Paytm), and bank transfer. Payment is collected after the massage session.',
            icon: <Phone size={24} />,
        },
        {
            question: 'Which areas in Bengaluru do you serve?',
            answer: 'We cover most areas in Bengaluru including JP Nagar, Jayanagar, BTM Layout, Koramangala, HSR Layout, Bannerghatta Road, Electronic City, and nearby areas. Call to confirm your location.',
            icon: <MapPin size={24} />,
        },
        {
            question: 'Can I cancel or reschedule my appointment?',
            answer: 'Yes, you can cancel or reschedule. We request at least 2 hours advance notice so we can accommodate other clients. No cancellation charges.',
            icon: <Clock size={24} />,
        },
    ];

    const toggleFAQ = (index) => {
        setOpenIndex(openIndex === index ? null : index);
    };

    return (
        <section className="py-20 bg-white">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Section Header */}
                <div className="text-center mb-16">
                    <div className="inline-flex items-center bg-zen-gold/10 px-6 py-2 rounded-full mb-6">
                        <span className="text-coffee-900 font-semibold">Got Questions?</span>
                    </div>
                    <h2 className="text-4xl md:text-5xl font-display font-bold text-coffee-900 mb-4">
                        Frequently Asked Questions
                    </h2>
                    <p className="text-lg text-coffee-700">
                        Everything you need to know about our massage services
                    </p>
                </div>

                {/* FAQ List */}
                <div className="space-y-4">
                    {faqs.map((faq, index) => (
                        <div
                            key={index}
                            className="premium-card bg-gradient-to-br from-white to-cream rounded-2xl shadow-md overflow-hidden"
                        >
                            {/* Question Button */}
                            <button
                                onClick={() => toggleFAQ(index)}
                                className="w-full flex items-center justify-between p-6 text-left hover:bg-zen-gold/5 transition-colors"
                            >
                                <div className="flex items-center gap-4 flex-1">
                                    <div className="text-zen-gold flex-shrink-0">
                                        {faq.icon}
                                    </div>
                                    <h3 className="text-lg font-bold text-coffee-900">
                                        {faq.question}
                                    </h3>
                                </div>
                                <ChevronDown
                                    size={24}
                                    className={`text-zen-gold transition-transform flex-shrink-0 ${openIndex === index ? 'rotate-180' : ''
                                        }`}
                                />
                            </button>

                            {/* Answer */}
                            {openIndex === index && (
                                <div className="px-6 pb-6 pt-2">
                                    <div className="pl-12">
                                        <p className="text-coffee-700 leading-relaxed">
                                            {faq.answer}
                                        </p>
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}
                </div>

                {/* Still have questions CTA */}
                <div className="mt-12 text-center bg-gradient-to-br from-zen-gold/10 to-zen-gold/5 p-8 rounded-2xl">
                    <h3 className="text-2xl font-display font-bold text-coffee-900 mb-4">
                        Still have questions?
                    </h3>
                    <p className="text-coffee-700 mb-6">
                        Call or WhatsApp us directly. We're here to help!
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                        <a
                            href="tel:+919901853969"
                            className="inline-flex items-center justify-center gap-2 bg-zen-gold text-white px-8 py-4 rounded-full font-semibold hover:bg-coffee-900 transition-all duration-300 shadow-premium"
                        >
                            <Phone size={20} />
                            Call: +91-9901853969
                        </a>
                        <a
                            href="https://wa.me/919901853969?text=Hi,%20I%20have%20a%20question%20about%20massage%20services"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center justify-center gap-2 bg-green-600 text-white px-8 py-4 rounded-full font-semibold hover:bg-green-700 transition-all duration-300 shadow-premium"
                        >
                            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.890-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
                            </svg>
                            WhatsApp Us
                        </a>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default FAQ;
